import { QuestionAnswer } from "@prisma/client";
import { staticPath } from "faqSrc/lib/$path";
import React from "react";
import styled from "styled-components";
import Image from "components/atoms/Image";
import IconExplode from "~/components/icons/IconExplode";
import {
  likeButtonExplode,
  likeButtonPulse,
  snsIconLabel,
} from "~/styles/CommonStyles";
import { getHelpfulStoreKeyForQa } from "./HelpfulButton";

type ButtonSize = "small" | "medium";
const heightMap: { [key in ButtonSize]: number } = {
  medium: 48,
  small: 40,
};
const iconSizeMap: { [key in ButtonSize]: number } = {
  medium: 28,
  small: 22,
};

interface Props {
  helpfulCount?: number;
  size?: ButtonSize;
}

// helpful button has many instance so use class instead of id
// each amp script wrap only one helpful button so it is no problem to use class
const HELPFUL_BUTTON_CLASS = "amp-helpful-button";
const HELPFUL_BUTTON_LABEL_CLASS = "amp-helpful-button-label";
const BEFORE_HELPFUL_BUTTON_CLASS = "amp-before-helpful-button";
const AFTER_HELPFUL_BUTTON_CLASS = "amp-after-helpful-button";

const AmpHelpfulButton: React.FC<Props> = ({
  helpfulCount,
  size = "small",
}) => {
  return (
    <HelpfulButton>
      <HelpfulIcon
        aria-label="Helpful Button"
        className={HELPFUL_BUTTON_CLASS}
        title="Helpful Button"
        dataSize={size}
      >
        <span className={BEFORE_HELPFUL_BUTTON_CLASS}>
          <Image
            alt="Helpful"
            ampLayout="fixed"
            height={iconSizeMap[size]}
            layout="fixed"
            src={staticPath.img.icon_heart_outlined_svg}
            width={iconSizeMap[size]}
          />
        </span>
        <span
          className={AFTER_HELPFUL_BUTTON_CLASS}
          style={{ visibility: "hidden" }}
        >
          <IconExplodeWrap className="amp-explode-svg">
            <IconExplode width={36} height={36} />
          </IconExplodeWrap>
          <Image
            alt="Helpful"
            ampLayout="fixed"
            height={iconSizeMap[size]}
            layout="fixed"
            src={staticPath.img.icon_heart_pink_svg}
            width={iconSizeMap[size]}
          />
        </span>
      </HelpfulIcon>
      {helpfulCount !== undefined && (
        <IconLabel className={HELPFUL_BUTTON_LABEL_CLASS}>
          {helpfulCount > 999 ? "999+" : helpfulCount}
        </IconLabel>
      )}
    </HelpfulButton>
  );
};

// amp-script is not allowed to be nested under another amp-script tag,
// thus combining all the necessary scripts into one parent script.
export function generateAmpHelpfulButtonScriptString({
  helpfulCount,
  increaseFeedbackHelpfulUrl,
  questionAnswerId,
}: {
  helpfulCount?: number;
  increaseFeedbackHelpfulUrl?: string;
  questionAnswerId: QuestionAnswer["id"];
}) {
  return `
    const helpfulButton = document.querySelector('.${HELPFUL_BUTTON_CLASS}');

    const itemStoreKey = "${getHelpfulStoreKeyForQa({
      questionAnswerId,
    })}";

    // check if qa has been increased before.
    const initialHasIncreased = getHasIncreasedHelpfulFromLocalStorage();

    // prevent users to actually increase helpful count multiple times by toggling button
    let hasIncreased = initialHasIncreased ? true : false;

    helpfulButton.addEventListener("click", handleHelpfulButtonClick);

    function handleHelpfulButtonClick() {
        const currentHasIncreased = getHasIncreasedHelpfulFromLocalStorage();
        const increaseFeedbackHelpfulUrl = "${increaseFeedbackHelpfulUrl}";

        // only increase helpful if it hasn't been increased previously.
        if(increaseFeedbackHelpfulUrl && !currentHasIncreased && !hasIncreased){
            hasIncreased = true;
            fetch(increaseFeedbackHelpfulUrl);
        }

        if(currentHasIncreased) {
        updateHelpfulButton(false);
        removeHasIncreasedHelpfulFromLocalStorage();
        } else {
        updateHelpfulButton(true);
        setHasIncreasedHelpfulToLocalStorage();
        }
    }

    function updateHelpfulButton(isIncreased){
        const beforeHelpfulIcon = document.querySelector(".${BEFORE_HELPFUL_BUTTON_CLASS}");
        const afterHelpfulIcon = document.querySelector(".${AFTER_HELPFUL_BUTTON_CLASS}");
        const explodeSvg = document.querySelector(".amp-explode-svg");

        beforeHelpfulIcon.style.visibility = isIncreased ? "hidden" : "visible";
        afterHelpfulIcon.style.visibility = isIncreased ? "visible" : "hidden";
        if (isIncreased) {
          afterHelpfulIcon.classList.add("animation");
          explodeSvg.classList.add("animation");
        } else {
          afterHelpfulIcon.classList.remove("animation");
          explodeSvg.classList.remove("animation");
        }
        updateHelpfulLabel(isIncreased);
    }

    function setHasIncreasedHelpfulToLocalStorage(){
        localStorage.setItem(itemStoreKey, "1");
    }

    function removeHasIncreasedHelpfulFromLocalStorage(){
        localStorage.setItem(itemStoreKey, "0");
    }

    function getHasIncreasedHelpfulFromLocalStorage(){
        const value = localStorage.getItem(itemStoreKey);

        if(value && value !== "0"){
        return true;
        }

        return false;
    }

    function updateHelpfulLabel(isIncreased){
      const label = document.querySelector(".${HELPFUL_BUTTON_LABEL_CLASS}");
      if (!label) {
        return;
      }
      let helpfulCount = ${helpfulCount ? helpfulCount : 0};
      // increase the count when has increased status in the local storage is true
      if (initialHasIncreased){
        helpfulCount = helpfulCount + 1;
      }
      let count = helpfulCount;
      if (initialHasIncreased){
        count = isIncreased ? helpfulCount : helpfulCount - 1;
      } else {
        count = isIncreased ? helpfulCount + 1 : helpfulCount;
      }
      // prevent invalid count
      if (count < 0) {
        count = 0;
      }
      label.textContent = count > 999 ? "999+" : count.toString();
    }
`;
}

const HelpfulButton = styled.div`
  display: grid;
  gap: 6px;

  &:hover {
    opacity: 0.8;
  }
`;

const HelpfulIcon = styled.button<{ dataSize: ButtonSize }>`
  align-items: center;
  background-color: #fff;
  border-radius: 50%;
  border: 2px solid transparent;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: grid;
  height: ${(props) => heightMap[props.dataSize]}px;
  justify-content: center;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  width: ${(props) => heightMap[props.dataSize]}px;

  & > span {
    align-items: center;
    display: grid;
    inset: 0;
    justify-content: center;
    position: absolute;

    &.${AFTER_HELPFUL_BUTTON_CLASS}.animation {
      animation: 0.3s ease-in-out 0s 1 normal none running ${likeButtonPulse};
    }
  }
`;

const IconExplodeWrap = styled.span`
  left: 3px;
  right: 0;
  bottom: 0;
  opacity: 0;
  position: absolute;
  top: 4px;
  transition: all 0.2s ease 0s;
  &.animation {
    animation: 0.3s ease 0s 1 normal none running ${likeButtonExplode};
  }
`;

const IconLabel = styled.span`
  ${snsIconLabel};
`;

export default AmpHelpfulButton;
